/**
 * Match Detail Screen
 *
 * Detalhes de uma partida específica
 */
import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
	ArrowLeft,
	MapPin,
	Users,
	Calendar,
	Trash2,
	Clock,
	DollarSign,
} from 'lucide-react'
import Header from '../organisms/Header'
import Card from '../atoms/Card'
import Badge from '../atoms/Badge'
import Button from '../atoms/Button'
import Avatar from '../atoms/Avatar'
import { LoadingScreen } from '../atoms/Spinner'
import { ConfirmModal } from '../molecules/Modal'
import DataTable from '../organisms/DataTable'
import { matchesApi } from '../../core/admin.api'
import { ADMIN_ROUTES, MATCH_STATUS_LABELS, MATCH_STATUS_COLORS } from '../../core/admin.constants'
import { formatDate, formatCurrency } from '../../core/admin.utils'
import toast from 'react-hot-toast'

export default function MatchDetailScreen() {
	const { id } = useParams()
	const navigate = useNavigate()
	const [match, setMatch] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)

	const fetchMatch = useCallback(async () => {
		try {
			setLoading(true)
			const response = await matchesApi.getById(id)
			setMatch(response)
		} catch (err) {
			setError(err.response?.data?.message || 'Erro ao carregar partida')
		} finally {
			setLoading(false)
		}
	}, [id])

	useEffect(() => {
		fetchMatch()
	}, [fetchMatch])

	const handleDelete = async () => {
		setActionLoading(true)
		try {
			await matchesApi.delete(id)
			toast.success('Partida excluída com sucesso')
			navigate(ADMIN_ROUTES.MATCHES)
		} catch (err) {
			toast.error(err.response?.data?.message || 'Erro ao excluir partida')
		} finally {
			setActionLoading(false)
		}
	}

	if (loading) {
		return (
			<div className="min-h-screen">
				<Header title="Carregando..." />
				<LoadingScreen />
			</div>
		)
	}

	if (error || !match) {
		return (
			<div className="min-h-screen">
				<Header title="Erro" />
				<div className="p-6">
					<Card className="text-center py-12">
						<p className="text-red-600 mb-4">{error || 'Partida não encontrada'}</p>
						<Button onClick={() => navigate(ADMIN_ROUTES.MATCHES)}>
							Voltar
						</Button>
					</Card>
				</div>
			</div>
		)
	}

	// Colunas para jogadores
	const playerColumns = [
		{
			key: 'player',
			label: 'Jogador',
			render: (row) => (
				<div className="flex items-center gap-3">
					<Avatar
						src={row.avatar}
						name={row.name}
						size="sm"
					/>
					<div>
						<p className="font-medium text-text">{row.name} {row.lastname || ''}</p>
						<p className="text-sm text-text-body">{row.email}</p>
					</div>
				</div>
			),
		},
		{
			key: 'rating',
			label: 'Avaliação',
			render: (row) => (
				<span className="text-text-body">
					{row.avgRating ? `${row.avgRating.toFixed(1)}/10` : '-'}
				</span>
			),
		},
	]

	const status = match.status || 'scheduled'

	return (
		<div className="min-h-screen">
			<Header
				title="Detalhes da Partida"
				actions={
					<Button
						variant="outline"
						size="sm"
						onClick={() => navigate(ADMIN_ROUTES.MATCHES)}
					>
						<ArrowLeft className="w-4 h-4 mr-2" />
						Voltar
					</Button>
				}
			/>

			<div className="p-6 space-y-6">
				{/* Header da partida */}
				<Card className="p-6">
					<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
						<div className="flex items-center gap-4">
							<div className="p-3 bg-primary/20 rounded-lg">
								<Calendar className="w-8 h-8 text-primary" />
							</div>
							<div>
								<h2 className="text-xl font-bold text-text">
									{match.club?.name || 'Partida'}
								</h2>
								<p className="text-text-body flex items-center gap-1">
									<Clock className="w-4 h-4" />
									{formatDate(match.date, "EEEE, dd 'de' MMMM 'de' yyyy 'às' HH:mm")}
								</p>
								<div className="flex items-center gap-4 mt-2">
									<Badge variant={MATCH_STATUS_COLORS[status] || 'gray'}>
										{MATCH_STATUS_LABELS[status] || status}
									</Badge>
									<Badge variant="blue">
										<Users className="w-3 h-3 mr-1" />
										{match.players?.length || 0} jogadores
									</Badge>
								</div>
							</div>
						</div>

						<Button
							variant="danger"
							size="sm"
							onClick={() => setShowDeleteModal(true)}
						>
							<Trash2 className="w-4 h-4 mr-2" />
							Excluir
						</Button>
					</div>
				</Card>

				{/* Informações */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* Dados da partida */}
					<Card className="p-6">
						<h3 className="text-lg font-semibold mb-4">Informações</h3>
						<div className="space-y-4">
							<InfoRow
								icon={Calendar}
								label="Criada em"
								value={formatDate(match.createdAt, "dd/MM/yyyy 'às' HH:mm")}
							/>
							{match.location?.name && (
								<InfoRow
									icon={MapPin}
									label="Local"
									value={match.location.name}
								/>
							)}
							{match.cost !== undefined && (
								<InfoRow
									icon={DollarSign}
									label="Custo"
									value={formatCurrency(match.cost)}
								/>
							)}
							{match.duration && (
								<InfoRow
									icon={Clock}
									label="Duração"
									value={`${match.duration} minutos`}
								/>
							)}
						</div>
					</Card>

					{/* Clube */}
					<Card className="p-6">
						<h3 className="text-lg font-semibold mb-4">Clube</h3>
						{match.club ? (
							<div
								className="flex items-center gap-4 p-4 bg-base-400 rounded-lg cursor-pointer hover:bg-base-400/80 transition-colors"
								onClick={() => navigate(`${ADMIN_ROUTES.CLUBS}/${match.club._id}`)}
							>
								<Avatar
									src={match.club.image}
									name={match.club.name}
									size="lg"
								/>
								<div>
									<p className="font-medium text-text">{match.club.name}</p>
								</div>
							</div>
						) : (
							<p className="text-text-body">Sem clube definido</p>
						)}
					</Card>
				</div>

				{/* Jogadores */}
				<Card className="p-6">
					<h3 className="text-lg font-semibold mb-4">
						Jogadores ({match.players?.length || 0})
					</h3>
					{match.players && match.players.length > 0 ? (
						<DataTable
							columns={playerColumns}
							data={match.players}
							emptyMessage="Nenhum jogador"
						/>
					) : (
						<p className="text-text-body text-center py-8">
							Nenhum jogador confirmado
						</p>
					)}
				</Card>

				{/* Times (se houver) */}
				{match.teams && match.teams.length > 0 && (
					<Card className="p-6">
						<h3 className="text-lg font-semibold mb-4">Times</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{match.teams.map((team, idx) => (
								<div key={team._id || idx} className="p-4 bg-base-400 rounded-lg">
									<h4 className="font-medium text-text mb-3">
										{team.name || `Time ${idx + 1}`}
									</h4>
									<div className="space-y-2">
										{team.players?.map((player) => (
											<div key={player._id} className="flex items-center gap-2">
												<Avatar
													src={player.avatar}
													name={player.name}
													size="sm"
												/>
												<span className="text-sm text-text-body">
													{player.name} {player.lastname || ''}
												</span>
											</div>
										))}
									</div>
								</div>
							))}
						</div>
					</Card>
				)}
			</div>

			{/* Modal de confirmação de exclusão */}
			<ConfirmModal
				isOpen={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={handleDelete}
				title="Excluir Partida"
				message={`Tem certeza que deseja excluir esta partida? Todos os dados relacionados (times, avaliações, etc.) serão removidos. Esta ação não pode ser desfeita.`}
				confirmText="Excluir"
				variant="danger"
				loading={actionLoading}
			/>
		</div>
	)
}

// Componente auxiliar para linha de informação
function InfoRow({ icon: Icon, label, value }) {
	return (
		<div className="flex items-start gap-3">
			<div className="p-2 bg-base-400 rounded-lg">
				<Icon className="w-4 h-4 text-text-body" />
			</div>
			<div>
				<p className="text-sm text-text-body">{label}</p>
				<p className="font-medium text-text">{value}</p>
			</div>
		</div>
	)
}
